import * as React from "react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { DateInput } from "@/components/ui/date-input";
import { CategorySelector } from "@/components/CategorySelector";
import { AccountSelector } from "@/components/AccountSelector";
import { CostCenterSelector } from "@/components/CostCenterSelector";
import { PaymentMethodSelector } from "@/components/PaymentMethodSelector";
import { SupplierSelector } from "@/components/SupplierSelector";
import { useFinance, type TransactionType } from "@/context/FinanceContext";

interface Category {
  id: string;
  name: string;
  type?: string;
  level2Id?: string;
  code?: string;
  hidden?: boolean;
}

interface Account {
  id: string;
  name: string;
  color?: string;
  initialBalance?: number;
}

interface Company {
  id: string;
  name: string;
  acronym: string;
}

interface CostCenter {
  id: string;
  name: string;
}

interface PaymentMethod {
  id: string;
  name: string;
}

interface Supplier {
  id: string;
  name: string;
}

interface BulkEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCount: number;
  transactionType?: TransactionType;
  categories: Category[];
  accounts: Account[];
  companies?: Company[];
  costCenters?: CostCenter[];
  paymentMethods?: PaymentMethod[];
  suppliers?: Supplier[];
  onApply: (data: {
    categoryId?: string;
    accountId?: string;
    companyId?: string;
    costCenterId?: string;
    paymentMethodId?: string;
    supplierId?: string;
    issueDate?: string;
    dueDate?: string;
    paymentDate?: string;
  }) => void;
}

export const BulkEditDialog: React.FC<BulkEditDialogProps> = ({
  open,
  onOpenChange,
  selectedCount,
  transactionType,
  categories,
  accounts,
  companies = [],
  costCenters = [],
  paymentMethods = [],
  suppliers = [],
  onApply,
}) => {
  const { addSupplier, addCostCenter, addPaymentMethod, addAccount } = useFinance();
  const [categoryId, setCategoryId] = React.useState<string>("");
  const [accountId, setAccountId] = React.useState<string>("");
  const [companyId, setCompanyId] = React.useState<string>("");
  const [costCenterId, setCostCenterId] = React.useState<string>("");
  const [paymentMethodId, setPaymentMethodId] = React.useState<string>("");
  const [supplierId, setSupplierId] = React.useState<string>("");
  const [issueDate, setIssueDate] = React.useState<string>("");
  const [dueDate, setDueDate] = React.useState<string>("");
  const [paymentDate, setPaymentDate] = React.useState<string>("");

  const availableCategories = React.useMemo(
    () => categories.filter(category => !category.hidden && (!transactionType || category.type === transactionType)),
    [categories, transactionType]
  );

  const handleAddAccount = async (name: string) => {
    const id = await addAccount(name);
    if (id) setAccountId(id);
  };

  const handleAddSupplier = async (name: string) => {
    const id = await addSupplier(name);
    if (id) setSupplierId(id);
  };

  const handleAddCostCenter = async (name: string) => {
    const id = await addCostCenter(name);
    if (id) setCostCenterId(id);
  };

  const handleAddPaymentMethod = async (name: string) => {
    const id = await addPaymentMethod(name);
    if (id) setPaymentMethodId(id);
  };

  const handleApply = () => {
    const data: any = {};
    if (categoryId && categoryId !== "__KEEP_CURRENT__") data.categoryId = categoryId;
    if (accountId && accountId !== "__KEEP_CURRENT__") data.accountId = accountId;
    if (companyId && companyId !== "__KEEP_CURRENT__") data.companyId = companyId;
    if (costCenterId && costCenterId !== "__KEEP_CURRENT__") data.costCenterId = costCenterId;
    if (paymentMethodId && paymentMethodId !== "__KEEP_CURRENT__") data.paymentMethodId = paymentMethodId;
    if (supplierId && supplierId !== "__KEEP_CURRENT__") data.supplierId = supplierId;
    if (issueDate) data.issueDate = issueDate;
    if (dueDate) data.dueDate = dueDate;
    if (paymentDate) data.paymentDate = paymentDate;
    
    onApply(data);
    
    // Clear form
    setCategoryId("");
    setAccountId("");
    setCompanyId("");
    setCostCenterId("");
    setPaymentMethodId("");
    setSupplierId("");
    setIssueDate("");
    setDueDate("");
    setPaymentDate("");
    onOpenChange(false);
  };

  const handleClear = () => {
    setCategoryId("");
    setAccountId("");
    setCompanyId("");
    setCostCenterId("");
    setPaymentMethodId("");
    setSupplierId("");
    setIssueDate("");
    setDueDate("");
    setPaymentDate("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="full" className="max-h-[95vh] overflow-y-auto">
        <DialogHeader className="bg-primary text-primary-foreground p-6 -m-6 mb-6 rounded-t-lg">
          <DialogTitle className="text-white text-xl">
            Edição em Lote — {transactionType === "receita" ? "Receitas" : "Despesas"} ({selectedCount} selecionados)
          </DialogTitle>
          <p className="text-primary-foreground/80 text-sm mt-2">
            Altere os campos desejados para aplicar as mudanças em todos os lançamentos selecionados.
          </p>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="space-y-2">
            <AccountSelector
              accounts={accounts}
              value={accountId}
              onValueChange={setAccountId}
              placeholder="Selecione uma conta (opcional)"
              required={false}
              canAddNew
              onAddNew={handleAddAccount}
            />
          </div>

          <CategorySelector
            categories={availableCategories}
            value={categoryId}
            onValueChange={setCategoryId}
            placeholder="Selecione um plano de contas (opcional)"
            filterType={transactionType}
            canAddNew={false}
            required={false}
          />

          <div className="space-y-2">
            <Label className="text-sm font-medium">Data de Emissão</Label>
            <DateInput
              value={issueDate}
              onChange={setIssueDate}
              placeholder="DD/MM/YYYY"
              className="w-full"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Data de Vencimento</Label>
            <DateInput
              value={dueDate}
              onChange={setDueDate}
              placeholder="DD/MM/YYYY"
              className="w-full"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Data de Pagamento</Label>
            <DateInput
              value={paymentDate}
              onChange={setPaymentDate}
              placeholder="DD/MM/YYYY"
              className="w-full"
            />
          </div>

          <SupplierSelector
            suppliers={suppliers}
            value={supplierId}
            onValueChange={setSupplierId}
            placeholder="Selecione um fornecedor (opcional)"
            required={false}
            canAddNew
            onAddNew={handleAddSupplier}
          />

          <CostCenterSelector
            costCenters={costCenters}
            value={costCenterId}
            onValueChange={setCostCenterId}
            placeholder="Selecione um centro de custos (opcional)"
            required={false}
            canAddNew
            onAddNew={handleAddCostCenter}
          />

          <PaymentMethodSelector
            paymentMethods={paymentMethods}
            value={paymentMethodId}
            onValueChange={setPaymentMethodId}
            placeholder="Selecione uma forma de pagamento (opcional)"
            required={false}
            canAddNew
            onAddNew={handleAddPaymentMethod}
          />
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <Button variant="outline" onClick={handleClear}>
            Limpar Campos
          </Button>
          <Button 
            onClick={handleApply}
            disabled={!categoryId && !accountId && !companyId && !costCenterId && !paymentMethodId && !supplierId && !issueDate && !dueDate && !paymentDate}
          >
            Aplicar Alterações
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
